import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, Chip, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Tooltip, CircularProgress, Stack,
  InputAdornment, useTheme,
} from '@mui/material';
import {
  Plus, Pencil, Trash2, Building2, Phone, MapPin,
  User, Search, X,
} from 'lucide-react';
import {
  subscribeToBranches, addBranch, updateBranch, deleteBranch,
} from '../firebase/branchService';

const emptyForm = { name: '', address: '', phone: '', managerName: '' };

const Branches = ({ currentUser }) => {
  const theme = useTheme();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [deletingBranch, setDeletingBranch] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const unsub = subscribeToBranches((data) => {
      setBranches(data);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const filtered = branches.filter((b) =>
    b.name?.toLowerCase().includes(search.toLowerCase()) ||
    b.address?.toLowerCase().includes(search.toLowerCase())
  );

  const openAdd = () => {
    setEditingBranch(null);
    setForm(emptyForm);
    setError('');
    setDialogOpen(true);
  };

  const openEdit = (branch) => {
    setEditingBranch(branch);
    setForm({
      name: branch.name || '',
      address: branch.address || '',
      phone: branch.phone || '',
      managerName: branch.managerName || '',
    });
    setError('');
    setDialogOpen(true);
  };

  const openDelete = (branch) => {
    setDeletingBranch(branch);
    setDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Branch name is required'); return; }
    setSaving(true);
    setError('');
    try {
      if (editingBranch) {
        await updateBranch(editingBranch.id, form);
        setSuccess('Branch updated');
      } else {
        await addBranch(form);
        setSuccess('Branch added');
      }
      setDialogOpen(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const handleDelete = async () => {
    if (!deletingBranch) return;
    setSaving(true);
    await deleteBranch(deletingBranch.id);
    setSaving(false);
    setDeleteDialogOpen(false);
    setDeletingBranch(null);
    setSuccess('Branch deleted');
    setTimeout(() => setSuccess(''), 3000);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Branches</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your shop locations
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Plus size={18} />} onClick={openAdd}>
          Add Branch
        </Button>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Search */}
      <TextField
        size="small"
        placeholder="Search branches..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 3, maxWidth: 320 }}
        InputProps={{
          startAdornment: <InputAdornment position="start"><Search size={16} /></InputAdornment>,
          endAdornment: search ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setSearch('')}><X size={14} /></IconButton>
            </InputAdornment>
          ) : null,
        }}
      />

      {/* Stats */}
      <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap' }}>
        {[
          { label: 'Total Branches', value: branches.length, color: 'primary' },
          { label: 'Active', value: branches.filter(b => b.status === 'active').length, color: 'success' },
        ].map((stat) => (
          <Paper key={stat.label} sx={{ px: 3, py: 2, minWidth: 140, textAlign: 'center' }}>
            <Typography variant="h4" fontWeight={700} color={`${stat.color}.main`}>{stat.value}</Typography>
            <Typography variant="caption" color="text.secondary">{stat.label}</Typography>
          </Paper>
        ))}
      </Stack>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Building2 size={48} color={theme.palette.text.disabled} />
          <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
            {search ? 'No branches match your search' : 'No branches yet'}
          </Typography>
          {!search && (
            <Button variant="contained" startIcon={<Plus size={18} />} onClick={openAdd} sx={{ mt: 2 }}>
              Add First Branch
            </Button>
          )}
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Branch Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Address</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Manager</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((branch) => (
                <TableRow key={branch.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Building2 size={16} color={theme.palette.primary.main} />
                      <Typography fontWeight={600}>{branch.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <MapPin size={14} color={theme.palette.text.secondary} />
                      <Typography variant="body2" color="text.secondary">
                        {branch.address || '—'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Phone size={14} color={theme.palette.text.secondary} />
                      <Typography variant="body2">{branch.phone || '—'}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <User size={14} color={theme.palette.text.secondary} />
                      <Typography variant="body2">{branch.managerName || '—'}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={branch.status === 'active' ? 'Active' : 'Inactive'}
                      color={branch.status === 'active' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(branch)}>
                          <Pencil size={16} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => openDelete(branch)}>
                          <Trash2 size={16} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingBranch ? 'Edit Branch' : 'Add New Branch'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="Branch Name *"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
              autoFocus
              InputProps={{ startAdornment: <InputAdornment position="start"><Building2 size={16} /></InputAdornment> }}
            />
            <TextField
              label="Address"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              fullWidth
              multiline
              rows={2}
              InputProps={{ startAdornment: <InputAdornment position="start"><MapPin size={16} /></InputAdornment> }}
            />
            <TextField
              label="Phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              fullWidth
              InputProps={{ startAdornment: <InputAdornment position="start"><Phone size={16} /></InputAdornment> }}
            />
            <TextField
              label="Manager Name"
              value={form.managerName}
              onChange={(e) => setForm({ ...form, managerName: e.target.value })}
              fullWidth
              InputProps={{ startAdornment: <InputAdornment position="start"><User size={16} /></InputAdornment> }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {saving ? 'Saving...' : editingBranch ? 'Update' : 'Add Branch'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Branch</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deletingBranch?.name}</strong>?
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {saving ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Branches;
